import React, { useState } from "react";
import { utils, writeFile } from "xlsx";

function StockReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [stockData, setStockData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Fetch stock report data
  const fetchStockReport = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const payload = {
      start_date: startDate,
      end_date: endDate,
    };

    fetch("https://stock.krishtec.co.in/stock/get_stock_report.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("Stock Report Data:", data);
        setStockData(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.log("Fetch stock report error:", err);
        setStockData([]);
      });
  };

  // Filter stock data based on search term
  const filteredStockData = stockData.filter((item) =>
    item.product_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Download report as Excel
  const downloadExcel = () => {
    if (filteredStockData.length === 0) {
      alert("No data to download.");
      return;
    }

    const worksheetData = filteredStockData.map((item) => ({
      "Start Date": formatDate(item.start_date),
      "End Date": formatDate(item.end_date),
      "Product ID": item.product_id,
      "Product Name": item.product_name,
      "Price Per Unit (Rs.)": parseFloat(item.price_per_unit).toFixed(2),
      "Total Purchased": item.total_purchased,
      "Total Sold": item.total_sold,
      "Stock at End": item.stock_at_end,
    }));

    const worksheet = utils.json_to_sheet(worksheetData);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Stock Report");
    worksheet["!cols"] = [
      { wch: 12 }, // Start Date
      { wch: 12 }, // End Date
      { wch: 12 }, // Product ID
      { wch: 20 }, // Product Name
      { wch: 15 }, // Price Per Unit
      { wch: 15 }, // Total Purchased
      { wch: 15 }, // Total Sold
      { wch: 15 }, // Stock at End
    ];
    writeFile(workbook, `Stock_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center p-6">
      <div className="flex flex-col gap-6 w-full max-w-6xl">
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 text-center">Stock Report</h1>

        {/* Date Range Inputs and Search Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex gap-4 items-end">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 w-40"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 w-40"
              />
            </div>
            <button
              onClick={fetchStockReport}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Fetch Report
            </button>
          </div>
          <div className="flex items-end gap-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Search Product</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by product name..."
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 w-64"
              />
            </div>
            <button
              onClick={downloadExcel}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Stock Report Table */}
        {filteredStockData.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Product ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Price Per Unit (Rs.)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Purchased
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Sold
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Stock at End
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStockData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">{formatDate(item.start_date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">{formatDate(item.end_date)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.product_id}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.product_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {parseFloat(item.price_per_unit).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.total_purchased}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.total_sold}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.stock_at_end}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          stockData.length === 0 && (
            <p className="text-center text-gray-500 mt-4">No data available. Please fetch the report.</p>
          )
        )}
      </div>
    </div>
  );
}

export default StockReport;